import React, { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import Layout from "./components/Layout";
import { Loading } from "./components/ui";
import { isDesktop } from "./lib/desktop";
import { AuthProvider, useAuth } from "./context/Auth";
import { CatalogProvider } from "./context/Catalog";
import Login from "./pages/Login";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Scrims = lazy(() => import("./pages/Scrims"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const SeriesDetail = lazy(() => import("./pages/SeriesDetail"));
const Replays = lazy(() => import("./pages/Replays"));
const MapDetail = lazy(() => import("./pages/MapDetail"));
const Watch = lazy(() => import("./pages/Watch"));
const Teams = lazy(() => import("./pages/Teams"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const Players = lazy(() => import("./pages/Players"));
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const Heroes = lazy(() => import("./pages/Heroes"));
const HeroDetail = lazy(() => import("./pages/HeroDetail"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Mistakes = lazy(() => import("./pages/Mistakes"));
const WeeklyReport = lazy(() => import("./pages/WeeklyReport"));
const Scouting = lazy(() => import("./pages/Scouting"));
const PrepLane = lazy(() => import("./pages/PrepLane"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const DraftSimulator = lazy(() => import("./pages/DraftSimulator"));
const Settings = lazy(() => import("./pages/Settings"));
const Capture = lazy(() => import("./pages/Capture"));
const Compare = lazy(() => import("./pages/Compare"));
const Heatmaps = lazy(() => import("./pages/Heatmaps"));
const Simulator = lazy(() => import("./pages/Simulator"));
const Canvas = lazy(() => import("./pages/Canvas"));
const Ranked = lazy(() => import("./pages/Ranked"));
const RankedPlayer = lazy(() => import("./pages/RankedPlayer"));

function Shell() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Login />;
  return (
    <CatalogProvider>
      <Routes>
        <Route path="/watch/:uid" element={<Suspense fallback={<Loading />}><Watch /></Suspense>} />
        <Route path="*" element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/scrims" element={<Scrims />} />
                <Route path="/tournaments" element={<Tournaments />} />
                <Route path="/series/:id" element={<SeriesDetail />} />
                <Route path="/replays" element={<Replays />} />
                <Route path="/maps/:uid" element={<MapDetail />} />
                <Route path="/teams" element={<Teams />} />
                <Route path="/teams/:id" element={<TeamDetail />} />
                <Route path="/players" element={<Players />} />
                <Route path="/players/:key" element={<PlayerDetail />} />
                <Route path="/heroes" element={<Heroes />} />
                <Route path="/heroes/:name" element={<HeroDetail />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/mistakes" element={<Mistakes />} />
                <Route path="/weekly" element={<WeeklyReport />} />
                <Route path="/scouting" element={<Scouting />} />
                <Route path="/prep" element={<PrepLane />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/ranked" element={<Ranked />} />
                <Route path="/ranked/players/:uid" element={<RankedPlayer />} />
                <Route path="/ratings" element={<Navigate to="/leaderboard" replace />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/heatmaps" element={<Heatmaps />} />
                <Route path="/simulator" element={<Simulator />} />
                <Route path="/canvas" element={<Canvas />} />
                <Route path="/draft" element={<DraftSimulator />} />
                <Route path="/settings" element={<Settings />} />
                {isDesktop && <Route path="/capture" element={<Capture />} />}
                <Route path="/match/:uid" element={<LegacyMatchRedirect />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
        } />
      </Routes>
    </CatalogProvider>
  );
}

function LegacyMatchRedirect() {
  const { uid } = useParams();
  return <Navigate to={`/maps/${uid}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}
